import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useVoiceCapture, type InterruptedSession } from '@/hooks/useVoiceCapture';
import {
  audioQueueMetaDeletionAllowed,
  useAudioQueueStore,
} from '@/lib/audio-queue-store';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import { recordingUnreadableHere } from '@/lib/audio-queue';
import type { ScriptureAnchor } from '@/types/prayer';
import styles from './ResumeRecordingCard.module.css';

interface Props {
  renderEntryChoice?: (queueItemId: string) => ReactNode;
  entryChoiceMade?: (queueItemId: string) => boolean;
  /**
   * Pre-load PrayPage textarea with text from a finished enhanced session.
   * `queueItemId` (DS, 2026-10-20): the recording the words come from; the
   * draft records that it holds it before the words move.
   */
  onContinueEnhanced: (preloadText: string, queueItemId: string) => boolean | Promise<boolean>;
  /** DS (2026-10-20): this recording was removed (Continue's close or Discard succeeded). */
  onRecordingRemoved?: (queueItemId: string) => void;
  /** Start the replacement recording only after the recovery source is gone. */
  onResumeEnhanced: () => void;
  /**
   * Create a Prayer with pendingTranscriptionId linking to the queue item.
   * `'reopen'` (LC8): not saved, because the recording's bytes cannot be read in
   * this page, so trying again here cannot succeed.
   */
  onSavePending: (
    queueItemId: string,
    scriptureAnchor?: ScriptureAnchor,
  ) => boolean | 'reopen' | Promise<boolean | 'reopen'>;
  /** Browser variant: restore home text or durably save an anchored prayer. */
  onKeepTyping: (
    capturedText: string,
    scriptureAnchor?: ScriptureAnchor,
  ) => boolean | Promise<boolean>;
}

/**
 * Resume card surfaced when the user returns to the app after an
 * interrupted recording (B.2). Stack semantics per E.12: always
 * renders the head of `interruptedSessions`. Dismiss pops the head,
 * the next session re-mounts the card automatically until the stack
 * empties.
 *
 * Two variants:
 *   - enhanced: blob captured during the interruption, the queue
 *     processor transcribes it asynchronously. Card waits for text
 *     when needed (R8). Buttons: "Continue recording" + "Save what
 *     I have" + "Discard".
 *   - browser: only interim text captured (Web Speech has no Blob).
 *     Buttons: "Keep typing" + "Discard". No "Continue recording"
 *     because we cannot resume what we cannot capture.
 *
 * z-index 250 — above PrayerDetail (110) so the card lands on top
 * regardless of which surface the user was on, below VoiceErrorToast
 * (500) so transcription errors still narrate over the card. Portal
 * to body for the layering invariant.
 *
 * No auto-dismiss timer per R6: this is a user-decision moment, not
 * a notification. Backdrop taps and Escape deliberately do nothing because
 * dismissing destroys the only recovery copy; only the explicit Discard or a
 * successful recovery action may pop the entry. One narrow exception (RU item
 * 3, 2026-10-20): after "Save what I have" was refused because the recording's
 * bytes cannot be read in this page (Save and Discard are then both refused
 * here and not offered), "Not now" closes the card for that durable recording
 * only, for this page and this owner's session (useVoiceCapture
 * deferInterruption). It removes, saves and persists nothing; Home shows an
 * entry for the recording, and the card offers it again after a reopen. When Enhanced Voice is off,
 * an audio-only entry remains visible but has no delete or retry action; Save
 * what I have can still bind it durably to a recoverable Prayer.
 */
export default function ResumeRecordingCard({
  renderEntryChoice,
  entryChoiceMade,
  onContinueEnhanced,
  onRecordingRemoved,
  onResumeEnhanced,
  onSavePending,
  onKeepTyping,
}: Props) {
  const { t } = useTranslation();
  const [choiceRequiredFor, setChoiceRequiredFor] = useState<string | null>(null);
  const entryChoiceRef = useRef<HTMLDivElement>(null);
  const [saveFailedFor, setSaveFailedFor] = useState<string | null>(null);
  // LC8: the failed Save of `saveFailedFor` was refused because its recording
  // cannot be read in this page (trying again here cannot succeed).
  const [saveNeedsReopen, setSaveNeedsReopen] = useState(false);
  // LC11: after that refusal the card no longer offers Save for that same
  // recording, and focus, which was on the removed Save, moves to the refusal
  // copy (never to Discard). Holds the session id the focus move is for.
  const reopenAlertRef = useRef<HTMLParagraphElement>(null);
  const focusReopenAlertRef = useRef<string | null>(null);
  // Fix round 1 (F2): Continue's words reached the Home draft, but its close
  // was then refused because this recording's bytes cannot be read in this
  // page (a race: the check before the words moved found them readable).
  const [movedUnreadableFor, setMovedUnreadableFor] = useState<string | null>(null);
  // Fix round 1 (F3): after "Not now" with another recording behind it, focus
  // goes into this card for the next recording (not to Home behind the modal).
  const cardRef = useRef<HTMLDivElement>(null);
  const focusNextCardRef = useRef(false);
  const [transferFailedFor, setTransferFailedFor] = useState<string | null>(null);
  // Once the destination write succeeds, a failed source cleanup may be
  // retried without authoring the draft/Prayer a second time.
  const [transferCommittedFor, setTransferCommittedFor] = useState<string | null>(null);
  const [actionInFlightFor, setActionInFlightFor] = useState<string | null>(null);
  // State disables the rendered buttons; the ref closes the smaller same-tick
  // window before React can publish that disabled state.
  const actionInFlightRef = useRef(false);
  // A Discard that did not happen (stream C, 2026-10-19; C2 adds the case of
  // no registry answer): the recording is kept, the card says so while that
  // same recording is still its offer, and focus returns to Discard if it was
  // there. Holds the session id the message and the focus return are for.
  const [discardFailedFor, setDiscardFailedFor] = useState<string | null>(null);
  const discardButtonRef = useRef<HTMLButtonElement>(null);
  const restoreDiscardFocusRef = useRef<string | null>(null);
  // useVoiceCapture is a context hook (returns the whole value
  // object), not a zustand store — destructure rather than passing a
  // selector. The audio-queue lookup below DOES use the zustand
  // selector pattern because that store is a real zustand slice.
  const { interruptedSessions: sessions, dismissInterruption, deferInterruption } = useVoiceCapture();
  const head: InterruptedSession | undefined = sessions[0];

  // Subscribe to the linked queue item (enhanced path) so transcribed-
  // text and failed-state changes trigger a re-render. Using a ref-
  // checked subscription rather than a derived selector — the items
  // array changes shape often, but per-item state changes (text fill,
  // failed flip) are the actual driver here.
  const queueItem = useAudioQueueStore((s) =>
    head?.mode === 'enhanced'
      ? s.items.find((i) => i.id === head.queueItemId)
      : undefined,
  );

  const runExclusiveAction = async (
    sessionId: string,
    action: () => Promise<void>,
  ): Promise<void> => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setActionInFlightFor(sessionId);
    try {
      await action();
    } finally {
      actionInFlightRef.current = false;
      setActionInFlightFor(null);
    }
  };

  const headId = head?.id ?? null;

  // The message only ever describes the Discard just attempted on the
  // recording now shown (stream C3, 2026-10-19). Session ids can repeat: the
  // legacy browser slot is always 'browser-interrupt', and an earlier head can
  // return after a newer session leaves by Continue or Save. So when the head
  // changes or leaves, an earlier failure is dropped, during render (React's
  // adjust-state-on-change pattern), before anything is shown for the new head.
  const [shownHeadId, setShownHeadId] = useState<string | null>(headId);
  if (shownHeadId !== headId) {
    setShownHeadId(headId);
    if (discardFailedFor !== null) setDiscardFailedFor(null);
  }

  // Runs after the buttons are enabled again (a disabled button cannot take
  // focus), and only while the same recording is still the card's head: focus
  // never moves to the Discard of a recording the person has not looked at.
  useEffect(() => {
    if (actionInFlightFor !== null || restoreDiscardFocusRef.current === null) return;
    const target = restoreDiscardFocusRef.current;
    restoreDiscardFocusRef.current = null;
    if (headId !== target) return;
    const button = discardButtonRef.current;
    const active = document.activeElement;
    if (button && (active === null || active === document.body || active === button)) {
      button.focus();
    }
  }, [actionInFlightFor, discardFailedFor, headId]);

  // Discard pops the head through dismissInterruption. The outcome is decided
  // here from the card's own offer, as the Home banner decides it from its
  // offer selection: anything but a confirmed removal is a Discard that did
  // not happen. That covers a rejection (dequeueAudio's session read happens
  // before its decision write; logged, never unhandled) and a false result:
  // no registry answer (the decision is withdrawn and the recording kept), or
  // a refusal. The message and the focus return apply only while this same
  // session is still the card's head (`discardFailedFor === head.id` below);
  // a refusal because the recording is no longer offered moves the card on
  // (it closes or shows the next session), so nothing is shown, as before.
  // Fix round 1 (F1, F2): the deterministic unreadable refusal, however it was
  // reached ("Save what I have", Continue's check, or a refused Discard): the
  // honest state the Save refusal shows (reopen copy, focus on it, "Not now",
  // no Save, Discard or Continue).
  const showUnreadableRefusal = (sessionId: string) => {
    setDiscardFailedFor(null);
    setTransferFailedFor(null);
    setSaveNeedsReopen(true);
    setSaveFailedFor(sessionId);
    focusReopenAlertRef.current = sessionId;
  };

  const discardHead = (sessionId: string, queueItemId?: string): Promise<void> => {
    const focusWasOnDiscard =
      discardButtonRef.current !== null && document.activeElement === discardButtonRef.current;
    return runExclusiveAction(sessionId, async () => {
      setDiscardFailedFor(null);
      let dismissed = false;
      try {
        dismissed = await dismissInterruption();
      } catch (error) {
        console.error(
          '[Adia] resume-discard-failed',
          error instanceof Error ? error.name : typeof error,
        );
      }
      if (dismissed) {
        setTransferFailedFor(null);
        setTransferCommittedFor(null);
        if (queueItemId) onRecordingRemoved?.(queueItemId);
        return;
      }
      // Fix round 1 (F2): a Discard refused because this page cannot read the
      // recording's bytes (dequeueAudio's own read) cannot succeed here; the
      // read-only check names exactly that refusal. Any other failure keeps
      // the existing copy and actions.
      if (queueItemId && await recordingUnreadableHere(queueItemId).catch(() => false)) {
        restoreDiscardFocusRef.current = null;
        showUnreadableRefusal(sessionId);
        return;
      }
      restoreDiscardFocusRef.current = focusWasOnDiscard ? sessionId : null;
      setDiscardFailedFor(sessionId);
    });
  };

  // LC11: once the buttons are enabled again, and only while the same recording
  // is still the card's head, focus left on the page body by the removed Save
  // goes to the refusal copy (tabIndex -1). Not to Discard: a destructive
  // button never takes focus by default. Discard stays reachable by Tab; with
  // Enhanced Voice off there is no Discard, and the copy keeps focus in the card.
  useEffect(() => {
    if (actionInFlightFor !== null || focusReopenAlertRef.current === null) return;
    const target = focusReopenAlertRef.current;
    focusReopenAlertRef.current = null;
    if (headId !== target) return;
    const copy = reopenAlertRef.current;
    const active = document.activeElement;
    // WebKit gives a tapped button's focus to the nearest focusable ancestor,
    // since fix round 1 the card container (tabIndex -1), not to the button.
    if (copy && (active === null || active === document.body || active === cardRef.current)) copy.focus();
  }, [actionInFlightFor, saveFailedFor, saveNeedsReopen, headId]);

  // Fix round 1 (F3): "Not now" with another recording behind it shows that
  // recording in this card, and focus moves into the card (the removed button
  // had it). With no recording left the card closes and Home takes focus.
  useEffect(() => {
    if (!focusNextCardRef.current) return;
    focusNextCardRef.current = false;
    if (!headId) return;
    const active = document.activeElement;
    if (active === null || active === document.body) cardRef.current?.focus();
  }, [headId]);

  useEffect(() => {
    if (!head) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Destructive dismissal requires the explicit Discard action.
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [head]);

  if (!head) return null;

  if (head.mode === 'browser') {
    return createPortal(
      <div
        className={styles.backdrop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-card-title"
      >
        <div ref={cardRef} className={styles.card} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
          <h2 id="resume-card-title" className={styles.title}>
            {t('voice.resumeCardTitleBrowser')}
          </h2>
          <blockquote className={styles.capturedText}>{head.capturedText}</blockquote>
          {transferFailedFor === head.id && (
            <p className={styles.saveError} role="alert">
              {t(
                transferCommittedFor === head.id
                  ? 'voice.resumeRecoveryCloseFailed'
                  : head.scriptureAnchor
                    ? 'voice.resumePrayerSaveFailed'
                    : 'voice.resumeTransferFailed',
              )}
            </p>
          )}
          {discardFailedFor === head.id && (
            <p className={styles.saveError} role="alert">{t('voice.discardFailed')}</p>
          )}
          <div className={styles.actions}>
            <button
              ref={discardButtonRef}
              type="button"
              className={styles.secondary}
              disabled={actionInFlightFor === head.id}
              onClick={() => discardHead(head.id)}
            >
              {t('voice.resumeDiscardAction')}
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={actionInFlightFor === head.id}
              onClick={() => runExclusiveAction(head.id, async () => {
                const alreadyCommitted = transferCommittedFor === head.id;
                const transferred = alreadyCommitted || (await onKeepTyping(
                  head.capturedText,
                  head.scriptureAnchor,
                ));
                if (transferred) {
                  setTransferCommittedFor(head.id);
                  const dismissed = await dismissInterruption();
                  if (dismissed) {
                    setTransferFailedFor(null);
                    setTransferCommittedFor(null);
                  } else {
                    setTransferFailedFor(head.id);
                  }
                } else {
                  setTransferFailedFor(head.id);
                }
              })}
            >
              {t(
                head.scriptureAnchor
                  ? 'voice.resumeSaveScriptureAction'
                  : 'voice.resumeKeepTypingAction',
              )}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Enhanced variant
  const transcribedText = queueItem?.transcribedText ?? null;
  const failed = queueItem?.failed ?? false;
  const parked =
    !enhancedVoiceAllowed() &&
    queueItem !== undefined &&
    !audioQueueMetaDeletionAllowed(queueItem, false);
  // R8: Continue button gates on text-ready state.
  //   - text ready  → enabled, primary action
  //   - still pending → disabled, "Finishing previous recording…"
  //   - failed      → disabled, swap copy to a static failure message
  //                   ("tap Save what I have / Discard")
  // Home compose has no durable Scripture-anchor draft companion. An anchored
  // interrupted session therefore cannot use Continue (which would silently
  // detach its passage); Save what I have preserves the frozen anchor.
  const continueDisabled =
    parked || !transcribedText || failed || Boolean(head.scriptureAnchor);
  // LC11, RU item 3 (2026-10-20): "Save what I have" was refused for this
  // recording because its bytes cannot be read in this page. Trying again here
  // cannot succeed, and Discard, which reads the same bytes before any write,
  // is refused here too; so neither is offered again for it (any other head
  // renders as before). The status line, whose parked and failed texts say to
  // tap Save, gives way to the refusal copy, and "Not now" sets the recording
  // aside for this page without changing it (deferInterruption), so the card
  // no longer blocks the app. A retryable failure keeps detail.saveError with
  // Save and Discard.
  const saveRefusedHere = saveFailedFor === head.id && saveNeedsReopen;
  // Fix round 1 (F2, the race): Continue's words are already in the Home
  // draft and its close was refused for the unreadable bytes.
  const movedUnreadableHere = movedUnreadableFor === head.id;
  // Fix round 1 (F1): in either state nothing on this card can succeed in
  // this page, Continue included (its close reads the same bytes): only the
  // copy and "Not now" are offered.
  const blockedHere = saveRefusedHere || movedUnreadableHere;
  const reopenCopyKey = movedUnreadableHere
    // The words are in the draft, which records this recording (DS,
    // 2026-10-20): after a reopen the draft's Save claims it, or refuses to
    // save its words twice, so the copy asks nothing of the person (no
    // "discard it later").
    ? 'voice.resumeMovedReopen'
    : saveRefusedHere
      // RU item 3: after a reopen the recording is offered again, here or,
      // once the registry merge has made it an inline offer, on Home (with its
      // passage since RU item 2). Reopening is advised as something to try
      // (item 3 of the DS follow-through), never promised to make Save work.
      // Parked, there is no Discard to name.
      ? parked ? 'voice.resumeSaveReopenParked' : 'voice.resumeSaveReopen'
      : null;
  const statusCopy = blockedHere
    ? null
    : parked
    ? t('voice.resumeParked')
    : failed
    ? t('voice.resumePreviousFailed')
    : !transcribedText
    ? t('voice.resumeWaitingForPrevious')
    : null;

  return createPortal(
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="resume-card-title"
    >
      <div ref={cardRef} className={styles.card} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <h2 id="resume-card-title" className={styles.title}>
          {t('voice.resumeCardTitleEnhanced')}
        </h2>
        {statusCopy && <p className={styles.statusCopy}>{statusCopy}</p>}
        {!blockedHere && !head.scriptureAnchor && renderEntryChoice && (
          <div ref={entryChoiceRef} tabIndex={-1}>
            {renderEntryChoice(head.queueItemId)}
            {choiceRequiredFor === head.id && !entryChoiceMade?.(head.queueItemId) &&
              <p className={styles.saveError} role="alert">{t('entry.chooseBeforeSave')}</p>}
          </div>
        )}
        {reopenCopyKey && (
          <p ref={reopenAlertRef} className={styles.saveError} role="alert" tabIndex={-1}>
            {t(reopenCopyKey)}
          </p>
        )}
        {!blockedHere && saveFailedFor === head.id && (
          <p className={styles.saveError} role="alert">{t('detail.saveError')}</p>
        )}
        {transferFailedFor === head.id && (
          <p className={styles.saveError} role="alert">
            {t(
              transferCommittedFor === head.id
                ? 'voice.resumeRecoveryCloseFailed'
                : 'voice.resumeTransferFailed',
            )}
          </p>
        )}
        {discardFailedFor === head.id && (
          <p className={styles.saveError} role="alert">{t('voice.discardFailed')}</p>
        )}
        <div className={styles.actions}>
          {!parked && !blockedHere && (
            <button
              ref={discardButtonRef}
              type="button"
              className={styles.secondary}
              disabled={actionInFlightFor === head.id}
              onClick={() => discardHead(head.id, head.queueItemId)}
            >
              {t('voice.resumeDiscardAction')}
            </button>
          )}
          {blockedHere && (
            <button
              type="button"
              className={styles.secondary}
              disabled={actionInFlightFor === head.id}
              onClick={() => {
                focusNextCardRef.current = true;
                deferInterruption(head.id, { wordsMoved: movedUnreadableHere });
              }}
            >
              {t('voice.resumeNotNowAction')}
            </button>
          )}
          {transferCommittedFor !== head.id && !blockedHere && (
            <button
              type="button"
              className={styles.secondary}
              disabled={actionInFlightFor === head.id}
              onClick={() => runExclusiveAction(head.id, async () => {
                if (!head.scriptureAnchor && entryChoiceMade && !entryChoiceMade(head.queueItemId)) {
                  setChoiceRequiredFor(head.id); entryChoiceRef.current?.focus(); return;
                }
                const saved = await onSavePending(
                  head.queueItemId,
                  head.scriptureAnchor,
                );
                setSaveNeedsReopen(saved === 'reopen');
                setSaveFailedFor(saved === true ? null : head.id);
                if (saved === 'reopen') focusReopenAlertRef.current = head.id;
              })}
            >
              {t('voice.resumeSaveAction')}
            </button>
          )}
          {!blockedHere && (
          <button
            type="button"
            className={styles.primary}
            disabled={continueDisabled || actionInFlightFor === head.id}
            onClick={() => runExclusiveAction(head.id, async () => {
              if (!transcribedText) return;
              const alreadyCommitted = transferCommittedFor === head.id;
              // Fix round 1 (F2): before the words move, a read-only check of
              // this recording's bytes. When this page cannot read them, the
              // close would be refused after the transfer, so nothing is
              // written and the card shows the unreadable refusal instead.
              // Rule #89's order is unchanged: the draft write is still proven
              // before the source is cleared.
              if (!alreadyCommitted && await recordingUnreadableHere(head.queueItemId).catch(() => false)) {
                showUnreadableRefusal(head.id);
                return;
              }
              const transferred = alreadyCommitted || (await onContinueEnhanced(
                transcribedText,
                head.queueItemId,
              ));
              if (transferred) {
                setTransferCommittedFor(head.id);
                const dismissed = await dismissInterruption();
                if (dismissed) {
                  setTransferFailedFor(null);
                  setTransferCommittedFor(null);
                  onRecordingRemoved?.(head.queueItemId);
                  onResumeEnhanced();
                } else if (await recordingUnreadableHere(head.queueItemId).catch(() => false)) {
                  // The race: the words are in the draft, and the close was
                  // refused for the unreadable bytes. Say so; never invite a
                  // second save of this recording.
                  setTransferFailedFor(null);
                  setMovedUnreadableFor(head.id);
                  focusReopenAlertRef.current = head.id;
                } else {
                  setTransferFailedFor(head.id);
                }
              } else {
                setTransferFailedFor(head.id);
              }
            })}
          >
            {t('voice.resumeContinueAction')}
          </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
